import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, Subscription, switchMap } from 'rxjs';
import { CaasContentService } from '../../services/caas-content.service';
import { ImageService } from '../../services/image.service';
import { PodcastItems, PodcastPlatforms } from 'hdi-components/source';
import {
  ImageMetaData,
  MenuItem,
  MetaData,
  ModalType,
  News,
  NewsFilter,
  safeAssign,
  ErrorHandlerService,
} from 'hdi-components';
import { ModalVideoService } from '../../services/modal-video.service';
import { SafeResourceUrl } from '@angular/platform-browser';
import { NavigationService } from '../../services/navigation.service';
import {
  CaasComponent,
  CaasLayout,
  CaasMainSection,
  CaasRoutes,
  CaasStage,
} from '../../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { DOCUMENT } from '@angular/common';

declare global {
  interface Window {
    TPP_SNAP: any;
  }
}

@Component({
  selector: 'app-standard-page',
  templateUrl: './standard-page.component.html',
  styleUrl: './standard-page.component.scss',
})
export class StandardPageComponent implements OnInit, OnDestroy {
  caasData: CaasLayout[] = [];
  mainSections?: CaasMainSection[];
  pageCaasSubscription$: Subscription = new Subscription();
  showModal = false;
  videoId?: string;
  videoSrc?: SafeResourceUrl;
  podcastData: { podcasts: PodcastItems[]; platforms: PodcastPlatforms[] } = {
    podcasts: [],
    platforms: [],
  };
  news: News[] = [];
  newsComponent: CaasComponent | undefined;
  newsFilters: { filter: string; categories: string[]; identifier: string }[] =
    [];
  filteredNews: News[] = [];
  imagesMetaData: ImageMetaData[] = [];
  alternateBackground: ('white' | 'grey')[] = [];
  events: CaasLayout[] = [];
  ModalType = ModalType;
  breadcrumbs: { title: string; url?: string }[] = [];
  inpageMenu: MenuItem[] = [];
  tags: { name: string; identifier: string }[] = [];

  private readonly document = inject(DOCUMENT);
  private router = inject(Router);

  constructor(
    private readonly caasContentService: CaasContentService,
    private readonly routes: ActivatedRoute,
    private readonly imageServices: ImageService,
    private readonly modalVideoService: ModalVideoService,
    private readonly navigationService: NavigationService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly navigationServices: NavigationService
  ) {}

  private hasComponent(sections: CaasMainSection[], uid: string): boolean {
    return sections.some(
      (section) =>
        Array.isArray(section?.formData?.st_items?.value) &&
        section.formData.st_items.value.some(
          (item) => item?.template?.uid === uid
        )
    );
  }

  private hasNewsFilterComponent(sections: CaasMainSection[]): boolean {
    return sections.some(
      (section) =>
        Array.isArray(section?.formData?.st_items?.value) &&
        section.formData.st_items.value.some(
          (item) => item?.formData?.st_layout?.value?.identifier === 'hub'
        )
    );
  }

  private getEmptyData() {
    return {
      content: { layouts: [], metaData: {} },
      podcastData: { podcasts: [], platforms: [] },
      news: [],
      newsFilters: [],
      tags: [],
      events: [],
      navData: [],
      imagesMetaData: [],
    };
  }

  private handleDataResponse({
    content,
    podcastData,
    news,
    newsFilters,
    tags,
    events,
    navData,
    imagesMetaData,
  }: {
    content: { layouts: CaasLayout[]; metaData: MetaData; identifier?: string };
    podcastData: { podcasts: PodcastItems[]; platforms: PodcastPlatforms[] };
    news: News[];
    newsFilters: NewsFilter[];
    tags: { identifier: string; name: string }[];
    events: CaasLayout[];
    navData: CaasRoutes[];
    imagesMetaData: ImageMetaData[];
  }) {
    this.addTPPIntegration(content.identifier);
    this.caasData = content.layouts;
    const mainLayout = content.layouts.find((layout) => layout.name === 'main');
    const children = mainLayout?.children as CaasMainSection[];

    this.mainSections = children?.filter((section) => {
      const item = section?.formData?.st_items?.value?.[0];
      const isNewsHub =
        item?.template?.uid === 'st_news' &&
        item?.formData?.st_layout?.value?.identifier === 'hub';

      if (isNewsHub) {
        this.newsComponent = item;
      }

      return !isNewsHub;
    });

    const stageLayout = content.layouts.find(
      (layout) => layout.name === 'stage'
    );
    if (stageLayout?.children?.[0]) {
      this.imageServices.replaceStageImages(
        stageLayout.children[0] as CaasStage
      );
    }

    if (this.mainSections) {
      this.imageServices.replaceMainImages(this.mainSections);
    }

    this.podcastData = podcastData;
    this.news = news;
    this.newsFilters = newsFilters;
    this.tags = tags;
    this.imagesMetaData = imagesMetaData;
    this.getAlternateBackground();
    this.inpageMenu = this.getInpageNav();

    if (this.news) {
      this.imageServices.replaceNewsImages(
        this.filteredNews.length > 0 ? this.filteredNews : this.news
      );
    }

    if (this.podcastData) {
      this.imageServices.replacePodcastsImages(this.podcastData.podcasts);
    }

    this.events = events;
    if (navData) {
      this.setBreadcrumbs(navData);
    }
  }

  ngOnInit() {
    this.openVideoModalByQueryParams();

    this.pageCaasSubscription$ = this.routes.data
      .pipe(
        switchMap((data) => {
          const pageIdentifier = data?.['pageIdentifier'];
          if (!pageIdentifier) {
            return of(this.getEmptyData());
          }

          return this.caasContentService.getContent(pageIdentifier).pipe(
            switchMap((content) => {
              const mainLayout = content.layouts.find(
                (layout) => layout.name === 'main'
              );
              const children =
                (mainLayout?.children as CaasMainSection[]) || [];

              const hasPodcasts = this.hasComponent(children, 'st_podcasts');
              const hasNews = this.hasComponent(children, 'st_news');
              const hasNewsFilter = this.hasNewsFilterComponent(children);

              return forkJoin({
                content: of(content),
                podcastData: hasPodcasts
                  ? this.caasContentService.getPodcastData()
                  : of({ podcasts: [], platforms: [] }),
                news: hasNews ? this.caasContentService.getNews() : of([]),
                newsFilters: hasNewsFilter
                  ? this.caasContentService.getNewsCategories()
                  : of([]),
                tags: hasNewsFilter
                  ? this.caasContentService.getTags()
                  : of([]),
                events: this.caasContentService.getEvents(),
                navData: this.navigationService.getCloudNavigation(),
                imagesMetaData: this.caasContentService.getImagesMetaData(),
              });
            })
          );
        })
      )
      .subscribe(this.handleDataResponse.bind(this));
  }
  getInpageNav(): MenuItem[] {
    return (this.mainSections ?? []).reduce((acc, section) => {
      if (
        section?.formData?.st_anchor_name?.value &&
        section?.formData?.st_inpagenav?.value
      ) {
        let title = '';
        let url = '';
        let hoverText = '';
        safeAssign(
          () => {
            title =
              section?.formData?.st_inpagenav?.value?.formData.lt_link_label
                .value ?? '';
          },
          'Set inpage nav title',
          this.errorHandler
        );
        safeAssign(
          () => {
            url = section?.formData?.st_anchor_name?.value ?? '';
          },
          'Set inpage nav url',
          this.errorHandler
        );
        safeAssign(
          () => {
            hoverText =
              section?.formData?.st_inpagenav?.value?.formData.lt_link_title
                ?.value ?? '';
          },
          'Set inpage nav hover text',
          this.errorHandler
        );
        acc.push({
          title: title,
          url: url,
          hoverText: hoverText,
        });
      }
      if (
        section.formData.st_items?.value &&
        section.formData.st_items.value.length > 0
      ) {
        section.formData.st_items.value.forEach((item) => {
          if (
            item.formData.st_anchor_name?.value &&
            item.formData.st_inpagenav?.value
          ) {
            let title = '';
            let url = '';
            let hoverText = '';
            safeAssign(
              () => {
                title =
                  item?.formData?.st_inpagenav?.value?.formData?.lt_link_label
                    ?.value ?? '';
              },
              'Set inpage nav title',
              this.errorHandler
            );
            safeAssign(
              () => {
                url = item?.formData?.st_anchor_name?.value ?? '';
              },
              'Set inpage nav url',
              this.errorHandler
            );
            safeAssign(
              () => {
                hoverText =
                  item?.formData?.st_inpagenav?.value?.formData?.lt_link_title
                    ?.value ?? '';
              },
              'Set inpage nav hover text',
              this.errorHandler
            );

            acc.push({
              title: title,
              url: url,
              hoverText: hoverText,
            });
          }
        });
      }
      return acc;
    }, [] as MenuItem[]);
  }

  openVideoModalByQueryParams(): void {
    const { showModal, videoId, videoSrc } =
      this.modalVideoService.openModalVideo();
    this.showModal = showModal;
    this.videoId = videoId;
    this.videoSrc = videoSrc;
  }

  updateNews(filteredNews: News[]): void {
    this.filteredNews = filteredNews;
  }
  getAlternateBackground() {
    let isWhite = (index: number) => index % 2 !== 0;
    this.mainSections?.forEach((section, index) => {
      if (section.formData.st_background_reset?.value) {
        this.alternateBackground.push('white');
        if (!isWhite(index) && !isWhite(0)) {
          isWhite = (index: number) => index % 2 === 0;
        } else {
          isWhite = (index: number) => index % 2 !== 0;
        }
      } else if (isWhite(index)) {
        this.alternateBackground.push('white');
      } else {
        this.alternateBackground.push('grey');
      }
    });
  }
  private setBreadcrumbs(navData: CaasRoutes[]): void {
    const currentPath = this.routes.snapshot.url
      .map((segment) => segment.path)
      .join('/');
    this.breadcrumbs = this.navigationService.generateBreadcrumbs(
      `/${currentPath}`,
      navData
    );
  }

  private addTPPIntegration(initialPreviewId: string | undefined): void {
    // TPP Integration Start
    if (this.document) {
      const main = this.document.querySelector('main');
      if (!main) {
        return;
      }
      main.setAttribute('data-preview-id', initialPreviewId || '');
      if (this.document.defaultView!.TPP_SNAP) {
        this.document.defaultView!.TPP_SNAP.onRequestPreviewElement(
          async (previewId: string) => {
            let previewSeoRoute: string | null;
            this.navigationServices
              .getCloudNavigation()
              .pipe()
              .subscribe((nav) => {
                previewSeoRoute =
                  this.navigationServices.getSeoRouteByIdentifier(
                    previewId.split('.')[0],
                    nav
                  );
                if (previewSeoRoute) {
                  this.router.navigate([previewSeoRoute]);
                }
              });
          }
        );
      }
      // TPP Integration End
    }
  }

  ngOnDestroy(): void {
    this.pageCaasSubscription$.unsubscribe();
  }
}
